/**
 * Minimal Code 128-B encoder, emitting inline SVG.
 *
 * Hand-rolled rather than pulled from a library because the pick sheet is built
 * as an HTML *string* written into a hidden iframe: a React barcode component
 * cannot render there, and the canvas-based encoders can't run in the node
 * environment vitest uses, so they'd be untestable. Code 128-B is a width table
 * plus a mod-103 checksum — small enough to own.
 *
 * Pure: no DOM, no React. `code128Svg` returns markup as a string.
 */

/**
 * The 107 Code 128 symbol patterns, as alternating bar/space module widths
 * starting with a bar. Indices 0-102 are data values, 103-105 are the Start
 * codes (A/B/C) and 106 is Stop.
 *
 * Every pattern is 11 modules wide except Stop, which is 13. `assertPatternTable`
 * checks exactly that, so a transcription slip fails a test rather than printing
 * an unscannable symbol.
 */
const PATTERNS: readonly string[] = [
  '212222', '222122', '222221', '121223', '121322', '131222', '122213', '122312',
  '132212', '221213', '221312', '231212', '112232', '122132', '122231', '113222',
  '123122', '123221', '223211', '221132', '221231', '213212', '223112', '312131',
  '311222', '321122', '321221', '312212', '322112', '322211', '212123', '212321',
  '232121', '111323', '131123', '131321', '112313', '132113', '132311', '211313',
  '231113', '231311', '112133', '112331', '132131', '113123', '113321', '133121',
  '313121', '211331', '231131', '213113', '213311', '213131', '311123', '311321',
  '331121', '312113', '312311', '332111', '314111', '221411', '431111', '111224',
  '111422', '121124', '121421', '141122', '141221', '112214', '112412', '122114',
  '122411', '142112', '142211', '241211', '221114', '413111', '241112', '134111',
  '111242', '121142', '121241', '114212', '124112', '124211', '411212', '421112',
  '421211', '212141', '214121', '412121', '111143', '111341', '131141', '114113',
  '114311', '411113', '411311', '113141', '114131', '311141', '411131', '211412',
  '211214', '211232', '2331112',
]

const START_B = 104
const STOP = 106
/** Code 128-B covers printable ASCII only. */
const MIN_CHAR = 32
const MAX_CHAR = 126

/** True when every character can be encoded in Code 128-B. */
export function isEncodable(value: string): boolean {
  if (value.length === 0) return false
  for (let i = 0; i < value.length; i++) {
    const c = value.charCodeAt(i)
    if (c < MIN_CHAR || c > MAX_CHAR) return false
  }
  return true
}

/**
 * Strip a string down to what Code 128-B can carry.
 *
 * Tenant-authored text (order references, destinations) can contain anything;
 * emitting a symbol for characters the charset cannot express would produce a
 * barcode that silently scans as the wrong thing.
 */
export function sanitizeForCode128(value: string): string {
  let out = ''
  for (let i = 0; i < value.length; i++) {
    const c = value.charCodeAt(i)
    if (c >= MIN_CHAR && c <= MAX_CHAR) out += value[i]
  }
  return out
}

/** The mod-103 check symbol: start value plus each value weighted by position. */
export function code128Checksum(values: number[]): number {
  let sum = START_B
  for (let i = 0; i < values.length; i++) sum += values[i] * (i + 1)
  return sum % 103
}

/**
 * Encode to a run-length list of module widths, alternating bar, space, bar…
 * (the first entry is always a bar).
 */
export function code128Bars(value: string): number[] {
  if (!isEncodable(value)) {
    throw new Error('Code 128-B can only encode printable ASCII (32-126)')
  }
  const values: number[] = []
  for (let i = 0; i < value.length; i++) values.push(value.charCodeAt(i) - MIN_CHAR)

  const symbols = [START_B, ...values, code128Checksum(values), STOP]
  const widths: number[] = []
  for (const s of symbols) {
    for (const ch of PATTERNS[s]) widths.push(Number(ch))
  }
  return widths
}

export interface Code128SvgOptions {
  /** Pixel width of one module. */
  moduleWidth?: number
  height?: number
  /** Bar colour. Space is always transparent so the sheet's paper shows. */
  color?: string
  /** Accessible name; defaults to the encoded value. */
  title?: string
}

/**
 * Render `value` as an inline SVG barcode.
 *
 * Returns an empty string for content that cannot be encoded — callers print
 * the human-readable text instead, which is better than an unscannable symbol.
 */
export function code128Svg(value: string, opts: Code128SvgOptions = {}): string {
  const moduleWidth = opts.moduleWidth ?? 1.6
  const height = opts.height ?? 38
  const color = opts.color ?? '#111827'

  if (!isEncodable(value)) return ''

  const widths = code128Bars(value)
  const totalModules = widths.reduce((a, b) => a + b, 0)
  const width = totalModules * moduleWidth

  let x = 0
  let rects = ''
  widths.forEach((w, i) => {
    // Even indices are bars, odd are spaces.
    if (i % 2 === 0) {
      rects += `<rect x="${round(x)}" y="0" width="${round(w * moduleWidth)}" height="${height}" fill="${color}"/>`
    }
    x += w * moduleWidth
  })

  const title = escapeXml(opts.title ?? value)
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${round(width)}" height="${height}" ` +
    `viewBox="0 0 ${round(width)} ${height}" role="img" aria-label="${title}">` +
    `<title>${title}</title>${rects}</svg>`
  )
}

/** Total module count of the symbol, useful for layout and for tests. */
export function code128ModuleCount(value: string): number {
  return code128Bars(value).reduce((a, b) => a + b, 0)
}

/**
 * Guard against a mistyped pattern table. Exported so a test can assert it
 * rather than relying on someone eyeballing 107 six-digit strings.
 */
export function assertPatternTable(): void {
  if (PATTERNS.length !== 107) {
    throw new Error(`Code 128 table must have 107 patterns, has ${PATTERNS.length}`)
  }
  PATTERNS.forEach((p, i) => {
    const sum = [...p].reduce((a, c) => a + Number(c), 0)
    const expected = i === STOP ? 13 : 11
    if (sum !== expected) {
      throw new Error(`Pattern ${i} spans ${sum} modules, expected ${expected}`)
    }
  })
}

function round(n: number): number {
  return Math.round(n * 1000) / 1000
}

function escapeXml(s: string): string {
  return s.replace(
    /[<>&"']/g,
    (c) =>
      ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&apos;' })[c] ?? c,
  )
}
